// components/ViewOnlyPortfolioTable.js
import React from 'react';
import {
  Typography,
  Paper,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import formatCurrency from '../utils/formatCurrency';

const ViewOnlyPortfolioTable = ({ holdings, privacyLevel, baseCurrency }) => {
  const theme = useTheme();
  const baseUrl = window.location.origin;

  // Columns for shared portfolios with full view (with quantities)
  const fullViewColumns = [
    {
      field: 'symbol',
      headerName: 'Company',
      minWidth: 180,
      flex: 1,
      renderCell: (params) => (
        <div>
          <RouterLink
            to={`${baseUrl}/asset/${params.row.symbol}`}
            style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
          >
            {params.row.symbol}
          </RouterLink>
          <div style={{ fontSize: '0.9em', color: '#888' }}>{params.row.name}</div>
        </div>
      ),
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'quantity',
      headerName: 'Position',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'price',
      headerName: 'Price',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => formatCurrency(params.value, params.row.currency)
    },
    {
      field: 'avgPrice',
      headerName: 'Avg Cost',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => formatCurrency(params.value, params.row.currency)
    },
    {
      field: 'dailyChangeAmount',
      headerName: 'Daily Change',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => (
        <div style={{ color: params.value > 0 ? theme.palette.success.main : params.value < 0 ? theme.palette.error.main : theme.palette.text.primary }}>
          {formatCurrency(params.value, params.row.currency)}
        </div>
      ),
    },
    {
      field: 'dailyChangePercentage',
      headerName: 'Daily %',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => (
        <div style={{ color: params.value > 0 ? theme.palette.success.main : params.value < 0 ? theme.palette.error.main : theme.palette.text.primary }}>
          {parseFloat(params.value).toFixed(2)}%
        </div>
      ),
    },
    {
      field: 'totalValue',
      headerName: 'Value',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => formatCurrency(params.row.totalValue, params.row.currency)
    },
    {
      field: 'pnlAmount',
      headerName: 'P&L',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => (
        <div style={{ color: params.value > 0 ? theme.palette.success.main : params.value < 0 ? theme.palette.error.main : theme.palette.text.primary }}>
          {formatCurrency(params.value, params.row.currency)}
        </div>
      ),
    },
    {
      field: 'pnlPercentage',
      headerName: 'P&L %',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => (
        <div style={{ color: params.value > 0 ? theme.palette.success.main : params.value < 0 ? theme.palette.error.main : theme.palette.text.primary }}>
          {parseFloat(params.value).toFixed(2)}%
        </div>
      ),
    },
  ];

  // Columns for limited view (public or shared with limited view)
  const limitedViewColumns = [
    {
      field: 'symbol',
      headerName: 'Symbol',
      minWidth: 120,
      flex: 1,
      renderCell: (params) => (
        <div>
          <div>{params.row.symbol}</div>
          <div style={{ fontSize: '0.9em', color: '#888' }}>{params.row.name}</div>
        </div>
      ),
    },
    {
      field: 'price',
      headerName: 'Price',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => formatCurrency(params.value, params.row.currency)
    },
    {
      field: 'dailyChangeAmount',
      headerName: 'Daily Change',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => (
        <div style={{ color: params.value > 0 ? theme.palette.success.main : params.value < 0 ? theme.palette.error.main : theme.palette.text.primary }}>
          {formatCurrency(params.value, params.row.currency)}
        </div>
      ),
    },
    {
      field: 'dailyChangePercentage',
      headerName: 'Daily %',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => (
        <div style={{ color: params.value > 0 ? theme.palette.success.main : params.value < 0 ? theme.palette.error.main : theme.palette.text.primary }}>
          {parseFloat(params.value).toFixed(2)}%
        </div>
      ),
    },
  ];

  // Determine which columns to use based on privacyLevel and presence of quantity field
  // This handles both the explicit privacyLevel and checking if the data actually contains the fields
  const hasFullData = holdings.length > 0 && 'quantity' in holdings[0];
  const columns = (privacyLevel === 'limited' || !hasFullData) ? limitedViewColumns : fullViewColumns;

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Holdings
      </Typography>

      {holdings.length > 0 ? (
        <DataGrid
          rows={holdings}
          getRowId={(row) => row.symbol}
          columns={columns}
          disableColumnMenu
          autoHeight
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: {
              sortModel: [{ field: 'symbol', sort: 'asc' }],
            },
          }}
          pageSizeOptions={[5, 10, 25]}
          sx={{
            '& .MuiDataGrid-cell': {
              fontSize: '1rem',
            },
            '& .MuiDataGrid-columnHeaders': {
              fontSize: '0.8rem',
            },
          }}
        />
      ) : (
        <Typography sx={{ mt: 2 }} variant="body1">No holdings in this portfolio.</Typography>
      )}
    </Paper>
  );
};

export default ViewOnlyPortfolioTable;